/** Vendor purpose taxonomy card (sites#349). No ECharts dependency in the page bundle. */
import { currentCycle } from './cycle-facet';
import { escapeHtml, formatCount, formatCurrency, formatPercent, graphql } from './leaf-detail';
import { loadMoneyFlow } from './vendor-clients';
import type { FlowLink, FlowNode } from './flow-types';

export interface PurposeGroup {
  groupId?: string | null;
  group: string;
  amount: number;
  count: number;
  clients: number | null;
  topPurposes: Array<{ purpose: string; amount: number; count: number }>;
}
export interface PurposeFlow {
  taxonomyVersion: string | null;
  totalAmount: number;
  groups: PurposeGroup[];
  links: Array<{
    committeeId: string; name: string | null; party: string | null;
    affinityDem: number | null; affinityRep: number | null;
    groupId?: string | null; group: string; amount: number; count: number;
  }>;
  otherLinks: Array<{ groupId?: string | null; group: string; amount: number; count: number }>;
}

// Fixed literal: included in the visitor-token persisted-query list at build time.
export const VENDOR_PURPOSE_FLOW_QUERY = `
  query VendorPurposeFlow($id: String!, $cycle: Int) {
    vendorPurposeFlow(vendorId: $id, cycle: $cycle) {
      taxonomyVersion totalAmount
      groups { groupId group amount count clients topPurposes { purpose amount count } }
      links { committeeId name party affinityDem affinityRep groupId group amount count }
      otherLinks { groupId group amount count }
    }
  }
`;

export async function fetchPurposeFlow(id: string, cycle: number | null = null): Promise<PurposeFlow | null> {
  const data = await graphql<{ vendorPurposeFlow: PurposeFlow | null }>(VENDOR_PURPOSE_FLOW_QUERY, { id, cycle });
  return data.vendorPurposeFlow;
}

export function positivePurposeTotal(groups: PurposeGroup[]): number {
  return groups.reduce((sum, g) => sum + Math.max(0, g.amount), 0);
}

const groupKey = (g: { group: string; groupId?: string | null }) => g.groupId || g.group;

export function purposeSummary(data: PurposeFlow | null): string {
  if (!data?.groups.length) return 'Not yet classified';
  const total = positivePurposeTotal(data.groups);
  if (!total) return 'No positive spending';
  return data.groups.filter(g => g.amount > 0).sort((a, b) => b.amount - a.amount).slice(0, 3)
    .map(g => `${g.group} (${formatPercent(g.amount / total)})`).join(', ');
}

export type PurposeSort = 'group' | 'amount' | 'share' | 'count' | 'clients';
export function sortedGroups(groups: PurposeGroup[], filter: string, key: PurposeSort, direction: 'asc' | 'desc'): PurposeGroup[] {
  const needle = filter.trim().toLocaleLowerCase();
  return groups.filter(g => g.group.toLocaleLowerCase().includes(needle) ||
    g.topPurposes.some(p => p.purpose.toLocaleLowerCase().includes(needle)))
    .sort((a, b) => {
      const order = key === 'group' ? a.group.localeCompare(b.group) :
        (a[key === 'share' ? 'amount' : key] ?? -Infinity) - (b[key === 'share' ? 'amount' : key] ?? -Infinity);
      return (direction === 'asc' ? order : -order) || a.group.localeCompare(b.group);
    });
}

/** Three explicit depths: committees → purpose groups → focal vendor. */
export function purposeFlow(vendor: { id: string; name: string }, data: PurposeFlow): { nodes: FlowNode[]; links: FlowLink[] } {
  const nodes: FlowNode[] = [];
  const links: FlowLink[] = [];
  const base = { fecId: null, party: null, affinityDem: null, affinityRep: null, otherCount: null };
  const groups = new Set<string>();
  for (const g of data.groups) {
    if (!Number.isFinite(g.amount) || g.amount <= 0) continue;
    const id = `purpose:${groupKey(g)}`;
    groups.add(groupKey(g));
    nodes.push({ ...base, id, name: g.group, side: 'mid', kind: 'Purpose', entityId: null, amount: g.amount, count: g.count });
    links.push({ source: id, target: 'focal', amount: g.amount, count: g.count, flow: 'payment' });
  }
  if (!groups.size) return { nodes: [], links: [] };
  nodes.push({ ...base, id: 'focal', side: 'focal', kind: 'Vendor', name: vendor.name, entityId: vendor.id, amount: null, count: null });
  const clients = new Map<string, FlowNode>();
  for (const l of data.links) {
    if (!groups.has(groupKey(l)) || !Number.isFinite(l.amount) || l.amount <= 0) continue;
    const id = `in:${l.committeeId}`;
    let node = clients.get(id);
    if (!node) {
      node = { ...base, id, side: 'in', kind: 'Committee', name: l.name || 'Unknown committee', entityId: l.committeeId,
        party: l.party, affinityDem: l.affinityDem, affinityRep: l.affinityRep, amount: 0, count: 0 };
      clients.set(id, node);
      nodes.push(node);
    }
    node.amount = (node.amount ?? 0) + l.amount;
    node.count = (node.count ?? 0) + l.count;
    links.push({ source: id, target: `purpose:${groupKey(l)}`, amount: l.amount, count: l.count, flow: 'payment' });
  }
  const others = data.otherLinks.filter(l => groups.has(groupKey(l)) && Number.isFinite(l.amount) && l.amount > 0);
  if (others.length) {
    nodes.push({ ...base, id: 'in:other', side: 'in', kind: 'Other', name: 'Other committees', entityId: null,
      amount: others.reduce((sum, l) => sum + l.amount, 0), count: others.reduce((sum, l) => sum + l.count, 0) });
    for (const l of others) links.push({ source: 'in:other', target: `purpose:${groupKey(l)}`, amount: l.amount, count: l.count, flow: 'payment' });
  }
  // Signed refunds can make a group's net total smaller than its positive
  // incoming flows. The diagram omits negative ribbons, so conserve only the
  // drawn positive flows; the table remains the authoritative signed net view.
  for (const node of nodes.filter(n => n.side === 'mid')) {
    const incoming = links.filter(l => l.target === node.id);
    const amount = incoming.reduce((sum, l) => sum + l.amount, 0);
    const count = incoming.reduce((sum, l) => sum + (l.count ?? 0), 0);
    node.amount = amount;
    node.count = count;
    const outgoing = links.find(l => l.source === node.id)!;
    outgoing.amount = amount;
    outgoing.count = count;
  }
  const drawn = links.filter(l => l.amount > 0);
  const used = new Set(drawn.flatMap(l => [l.source, l.target]));
  return { nodes: nodes.filter(n => used.has(n.id)), links: drawn };
}

export function rawPurposesHtml(raw: string | null): string {
  return `<details class="purpose-raw"><summary>Raw purposes</summary><p>${escapeHtml(raw || 'No raw purposes available.')}</p></details>`;
}

/** Keep cycle controls outside the replaceable chart/table region. A generation guards slow requests. */
export function mountVendorPurposes(vendor: { id: string; name: string; cycles: string | null; raw: string | null }, initial: PurposeFlow | null): void {
  const section = document.getElementById('purposes');
  if (!section) return;
  const cycles = [...new Set((vendor.cycles?.match(/\b\d{4}\b/g) || []).map(Number))]
    .filter(year => year >= 1976 && year <= 2100 && year % 2 === 0).sort((a, b) => b - a);
  const controls = document.createElement('div');
  controls.className = 'purpose-controls';
  controls.innerHTML = `<label for="purpose-cycle">Cycle</label> <select id="purpose-cycle" aria-label="Purpose cycle"><option value="">All cycles</option>${cycles.map(c => `<option value="${c}">${c}</option>`).join('')}</select>
    <p role="status" class="purpose-status"></p>`;
  section.appendChild(controls);
  // Starts on the global cycle facet (sites#194), the cycle `initial` was fetched for.
  const global = currentCycle();
  const select = controls.querySelector<HTMLSelectElement>('select')!;
  if (global) {
    if (!cycles.includes(global)) select.add(new Option(String(global), String(global)), 1);
    select.value = String(global);
  }
  const content = document.createElement('div');
  section.appendChild(content);
  const status = controls.querySelector<HTMLElement>('[role="status"]')!;
  let generation = 0;
  let key: PurposeSort = 'amount';
  let direction: 'asc' | 'desc' = 'desc';
  let filter = '';

  const render = async (data: PurposeFlow | null, token: number) => {
    window.electinfoMoneyFlow?.unmount?.(content);
    content.replaceChildren();
    if (!data?.groups.length) {
      const cycleSelected = Boolean(controls.querySelector<HTMLSelectElement>('select')!.value);
      content.innerHTML = `<p>${cycleSelected ? 'Cycle breakdown unavailable.' : 'Purpose breakdown is not available for this selection.'}</p>${rawPurposesHtml(vendor.raw)}`;
      return;
    }
    content.innerHTML = `<h3>Purpose distribution</h3><p>Net total: ${formatCurrency(data.totalAmount)} · Taxonomy: ${escapeHtml(data.taxonomyVersion || 'unknown')}</p><p class="purpose-share-note">Shares use positive group totals; negative amounts are net refunds or reclassifications. The Sankey shows positive flows only and may differ from net totals.</p>
      <div class="purpose-table-view"><label for="purpose-filter">Filter</label> <input id="purpose-filter" type="search" placeholder="Filter groups or raw purposes…" aria-label="Filter purpose groups">
      <div class="purpose-table-scroll" tabindex="0" role="region" aria-label="Purpose breakdown table"><table class="breakdown-table leaf-relationship-list"><thead><tr>${([
        ['group', 'Group'], ['amount', 'Amount'], ['share', '%'], ['count', 'Payments'], ['clients', 'Clients']
      ] as const).map(([sort, label]) => `<th scope="col" data-sort="${sort}"><button type="button">${label}</button></th>`).join('')}</tr></thead><tbody></tbody></table></div><p class="purpose-scroll-hint">Scroll horizontally to see all columns →</p><p class="purpose-empty" hidden>No matching purpose groups.</p></div>`;
    const list = content.querySelector<HTMLElement>('.purpose-table-view')!;
    const input = list.querySelector<HTMLInputElement>('input')!;
    input.value = filter;
    const positiveTotal = positivePurposeTotal(data.groups);
    const updateRows = () => {
      const rows = sortedGroups(data.groups, filter, key, direction);
      list.querySelector('tbody')!.innerHTML = rows.map((g, i) => `<tr class="purpose-group-row" data-group-id="${escapeHtml(groupKey(g))}"><td><details data-purpose-index="${i}"><summary aria-controls="purpose-raw-${i}">${escapeHtml(g.group)}</summary></details></td>
        <td class="num">${formatCurrency(g.amount)}</td><td class="num">${formatPercent(positiveTotal ? g.amount / positiveTotal : null)}</td><td class="num">${formatCount(g.count)}</td><td class="num">${formatCount(g.clients)}</td></tr>`).join('');
      list.querySelectorAll<HTMLDetailsElement>('details[data-purpose-index]').forEach(details => {
        details.addEventListener('toggle', () => {
          const i = Number(details.dataset.purposeIndex);
          const row = details.closest('tr')!;
          const existing = row.nextElementSibling;
          if (existing?.classList.contains('purpose-raw-row')) existing.remove();
          if (!details.open) return;
          const g = rows[i];
          const expanded = document.createElement('tr');
          expanded.className = 'purpose-raw-row';
          expanded.id = `purpose-raw-${i}`;
          expanded.innerHTML = `<td colspan="5">${g.topPurposes.length ?
            `<ul>${g.topPurposes.map(p => `<li><span>${escapeHtml(p.purpose)}</span> <span class="purpose-raw-amount">${formatCurrency(p.amount)} · ${formatCount(p.count)} payments</span></li>`).join('')}</ul>` : '<p>Raw-purpose detail is not available for this cycle.</p>'}</td>`;
          row.after(expanded);
        });
      });
      list.querySelector<HTMLElement>('.purpose-empty')!.hidden = rows.length > 0;
      list.querySelectorAll<HTMLElement>('th[data-sort]').forEach(th => th.setAttribute('aria-sort', th.dataset.sort === key ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'));
    };
    input.addEventListener('input', () => { filter = input.value; updateRows(); });
    list.querySelectorAll<HTMLElement>('th[data-sort]').forEach(th => th.querySelector('button')!.addEventListener('click', () => {
      const next = th.dataset.sort as PurposeSort;
      direction = next === key ? (direction === 'desc' ? 'asc' : 'desc') : (next === 'group' ? 'asc' : 'desc');
      key = next;
      updateRows();
    }));
    updateRows();
    try {
      await loadMoneyFlow();
      if (token !== generation || !content.isConnected) return;
      window.electinfoMoneyFlow?.mount({ section: content, heading: content.querySelector('h3')!, list, side: 'in', ...purposeFlow(vendor, data) });
    } catch (error) { console.warn('vendor purpose Sankey not rendered', error); }
  };
  void render(initial, generation);
  controls.querySelector('select')!.addEventListener('change', async event => {
    const value = (event.target as HTMLSelectElement).value;
    const token = ++generation;
    status.textContent = 'Loading purpose breakdown…';
    content.setAttribute('aria-busy', 'true');
    try {
      const data = await fetchPurposeFlow(vendor.id, value ? Number(value) : null);
      if (token !== generation) return;
      await render(data, token);
      status.textContent = '';
    } catch {
      if (token !== generation) return;
      await render(null, token);
      status.textContent = 'Unable to load purpose breakdown. Try another cycle.';
    } finally {
      if (token === generation) content.removeAttribute('aria-busy');
    }
  });
}
